/* ========== WORKBOOK CONTEXT LOADER ==========
 * Builds the lookup tables every chart parser needs:
 *  - sheet rId ↔ sheet name ↔ worksheet XML path
 *  - definedNames
 *  - sharedStrings table
 *  - cached cell maps (for resolving chart data-range formulas)
 *
 * Also provides readRangeValues() which converts a "Sheet!A1:B5" formula
 * into the actual cell values, used by the extended-chart parser and
 * sparkline parser to dereference data range refs.
 * ============================================== */

import { qLocal, qLocalAll } from "../xmlUtils";
import { parseCellRef, parseSheetRangeFormula } from "./cellRefs";

const REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

/**
 * Build a context object containing every workbook-level lookup the chart
 * parsers will need. Called once per parse run.
 */
export async function loadWorkbookContext(zip) {
  const ctx = {
    sheetIdToName: {},       // rId → "Sheet1"
    sheetPathToName: {},     // "worksheets/sheet1.xml" → "Sheet1"
    sheetNameToPath: {},     // "Sheet1" → "xl/worksheets/sheet1.xml"
    definedNames: {},        // workbook-level defined names
    sharedStrings: [],       // shared-string table
    sheetCellCache: {},      // memoization for getSheetCellMap
  };

  const wbXmlFile = zip.file("xl/workbook.xml");
  if (!wbXmlFile) return ctx;
  const wbXml = await wbXmlFile.async("string");
  const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");

  // <sheet name="..." r:id="..."/>
  qLocalAll(wbDoc, "sheet").forEach(node => {
    const rId = node.getAttribute("r:id") || node.getAttributeNS(REL_NS, "id");
    const name = node.getAttribute("name");
    if (rId && name) ctx.sheetIdToName[rId] = name;
  });

  // <definedName name="...">refs</definedName>
  qLocalAll(wbDoc, "definedName").forEach(node => {
    const name = node.getAttribute("name");
    const text = (node.textContent || "").trim();
    if (name) ctx.definedNames[name] = text;
  });

  // Match sheet rIds to their actual XML paths from workbook.xml.rels
  const wbRelsFile = zip.file("xl/_rels/workbook.xml.rels");
  if (wbRelsFile) {
    const wbRelsXml = await wbRelsFile.async("string");
    const wbRelsDoc = new DOMParser().parseFromString(wbRelsXml, "application/xml");
    qLocalAll(wbRelsDoc, "Relationship").forEach(rel => {
      const id = rel.getAttribute("Id");
      const target = rel.getAttribute("Target");
      if (!id || !target) return;
      if (ctx.sheetIdToName[id]) {
        const cleanTarget = target.replace(/^\/?xl\//, "");
        ctx.sheetPathToName[cleanTarget] = ctx.sheetIdToName[id];
        ctx.sheetNameToPath[ctx.sheetIdToName[id]] = "xl/" + cleanTarget;
      }
    });
  }

  // Shared strings table — used to resolve cell values of type "s"
  const ssFile = zip.file("xl/sharedStrings.xml");
  if (ssFile) {
    const ssXml = await ssFile.async("string");
    const ssDoc = new DOMParser().parseFromString(ssXml, "application/xml");
    qLocalAll(ssDoc, "si").forEach(si => {
      const tNodes = qLocalAll(si, "t");
      const text = tNodes.map(t => t.textContent || "").join("");
      ctx.sharedStrings.push(text);
    });
  }

  return ctx;
}

/**
 * Parse a worksheet's XML into a Map<"row:col", string-value>. Cached on ctx.
 */
export async function getSheetCellMap(zip, ctx, sheetPath) {
  if (!sheetPath) return new Map();
  if (ctx.sheetCellCache[sheetPath]) return ctx.sheetCellCache[sheetPath];

  const file = zip.file(sheetPath);
  if (!file) {
    ctx.sheetCellCache[sheetPath] = new Map();
    return ctx.sheetCellCache[sheetPath];
  }

  const xml = await file.async("string");
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const map = new Map();

  qLocalAll(doc, "row").forEach(rowEl => {
    qLocalAll(rowEl, "c").forEach(cEl => {
      const ref = cEl.getAttribute("r");
      if (!ref) return;
      const pos = parseCellRef(ref);
      if (!pos) return;
      const t = cEl.getAttribute("t") || "n";
      let value = "";

      if (t === "inlineStr") {
        const isNode = qLocal(cEl, "is");
        if (isNode) {
          const tNodes = qLocalAll(isNode, "t");
          value = tNodes.map(n => n.textContent || "").join("");
        }
      } else {
        const vNode = qLocal(cEl, "v");
        const raw = vNode ? (vNode.textContent || "") : "";
        if (t === "s") {
          const idx = parseInt(raw, 10);
          value = (Number.isFinite(idx) && ctx.sharedStrings[idx] != null) ? ctx.sharedStrings[idx] : "";
        } else {
          value = raw;
        }
      }
      map.set(pos.row + ":" + pos.col, value);
    });
  });

  ctx.sheetCellCache[sheetPath] = map;
  return map;
}

/**
 * Read every value in a "Sheet!A1:B5" formula range. Defined names are
 * dereferenced first. Returns [] if anything fails.
 */
export async function readRangeValues(zip, ctx, formulaOrName) {
  if (!formulaOrName) return [];
  let formula = formulaOrName.trim();
  if (ctx.definedNames[formula]) formula = ctx.definedNames[formula];

  const parsed = parseSheetRangeFormula(formula);
  if (!parsed) return [];
  const sheetPath = ctx.sheetNameToPath[parsed.sheet];
  if (!sheetPath) return [];
  const cells = await getSheetCellMap(zip, ctx, sheetPath);

  const values = [];
  const tl = parsed.tl;
  const br = parsed.br;
  for (let r = tl.row; r <= br.row; r++) {
    for (let c = tl.col; c <= br.col; c++) {
      values.push(cells.get(r + ":" + c) || "");
    }
  }
  return values;
}

/**
 * Read a range as a 2D grid: array of rows, each row is array of column
 * values. Used by Treemap/Sunburst which reference a multi-column label
 * range as the hierarchy (col 0 = parent, col 1 = child, …).
 *
 * Returns { rows, colCount } or { rows: [], colCount: 0 } on failure.
 */
export async function readRangeAsGrid(zip, ctx, formulaOrName) {
  if (!formulaOrName) return { rows: [], colCount: 0 };
  let formula = formulaOrName.trim();
  if (ctx.definedNames[formula]) formula = ctx.definedNames[formula];

  const parsed = parseSheetRangeFormula(formula);
  if (!parsed) return { rows: [], colCount: 0 };
  const sheetPath = ctx.sheetNameToPath[parsed.sheet];
  if (!sheetPath) return { rows: [], colCount: 0 };
  const cells = await getSheetCellMap(zip, ctx, sheetPath);

  const rows = [];
  const colCount = parsed.br.col - parsed.tl.col + 1;
  for (let r = parsed.tl.row; r <= parsed.br.row; r++) {
    const row = [];
    for (let c = parsed.tl.col; c <= parsed.br.col; c++) {
      row.push(cells.get(r + ":" + c) || "");
    }
    rows.push(row);
  }
  return { rows, colCount };
}
